import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from 'src/features/auth/AuthContext';

import LockedPage from 'src/pages/LockedPage';
import ChangePasswordPage from 'src/features/auth/ChangePasswordPage';
import LoginPage from 'src/features/auth/LoginPage';
import AllProjectsDashboard from 'src/pages/projects/AllProjectsDashboard';
import ProjectLayout from 'src/pages/projects/ProjectLayout';
import ProjectDashboard from 'src/pages/projects/ProjectDashboard';
import WbsPage from 'src/features/wbs/WbsPage';
import WbsProgressEntryPage from 'src/features/wbs/WbsProgressEntryPage';
import WbsInternalProgressEntryPage from 'src/features/wbs/WbsInternalProgressEntryPage';
import ProjectsPage from 'src/features/projects/ProjectsPage';
import WbsPublishPage from 'src/features/wbs/WbsPublishPage';
import WbsEditorPage from 'src/features/wbs/editor/WbsEditorPage';
import AdminOverviewPage from 'src/pages/admin/AdminOverviewPage';
import AdminCompaniesPage from 'src/pages/admin/AdminCompaniesPage';
import AdminUsersPage from 'src/pages/admin/AdminUsersPage';
import ProjectSettingsLayout from 'src/pages/projects/settings/ProjectSettingsLayout';
import ProjectSettingsGeneralPage from 'src/pages/projects/settings/ProjectSettingsGeneralPage';
import ProjectMembersPage from 'src/pages/projects/settings/ProjectMembersPage';
import ProjectWbsGroupsPage from 'src/pages/projects/settings/ProjectWbsGroupsPage';
import LandingPage from 'src/pages/LandingPage';
import AdminPermissionsPage from 'src/pages/admin/AdminPermissionsPage';
import AdminRolesPage from 'src/pages/admin/AdminRolesPage';
import CompanyAdminLayout from 'src/pages/company-admin/CompanyAdminLayout';
import CompanyProfilePage from 'src/pages/company-admin/CompanyProfilePage';
import CompanyUsersPage from 'src/pages/company-admin/CompanyUsersPage';
import CompanyProjectsPage from 'src/pages/company-admin/CompanyProjectsPage';
import CompanyPartnersPage from 'src/pages/company-admin/CompanyPartnersPage';
// import ProjectCompaniesPage from 'src/pages/projects/settings/ProjectCompaniesPage';
import ProjectCompaniesPage from 'src/pages/projects/settings/ProjectCompaniesPage';
import AdminLayout from 'src/pages/admin/AdminLayout';
import RiskPage from 'src/features/risks/RiskPage';
import TaskPage from 'src/features/tasks/TaskPage';
import GanttPage from 'src/features/gantt/GanttPage';
import ContractsHomePage from 'src/features/contracts/pages/ContractsHomePage';
import ContractDetailPage from 'src/features/contracts/pages/ContractDetailPage';
import PaymentExecutionPage from 'src/features/payments/pages/PaymentExecutionPage';
import PaymentsHomePage from 'src/features/payments/pages/PaymentsHomePage';
import ProjectFxSettingsPage from 'src/pages/projects/settings/ProjectFxSettingsPage';
import SCurvePage from 'src/features/s-curve/pages/SCurvePage';
import ProjectOrgsPage from 'src/pages/projects/settings/ProjectOrgsPage';
import RequireProjectPermission from 'src/features/projects/RequireProjectPermission';
import { ContractsPageV2, ContractDetailPageV2 } from 'src/features/contractsV2/pages';

function RequireAuth({ children }: { children: JSX.Element }) {
    const { me, loading, isLoggingOut } = useAuth();
    const loc = useLocation();
    if (loading) return <div className="p-6">세션 확인중…</div>;
    if (!me) {
        return (
            <Navigate
                to={isLoggingOut ? '/login?logged_out=1' : `/login?redirect=${encodeURIComponent(loc.pathname + loc.search)}`}
                replace
            />
        );
    }
    if (me.status === 'LOCKED') return <Navigate to="/locked" replace />;
    if (me.mustChangePassword) {
        return <Navigate to={`/change-password?redirect=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
    }

    return children;
}

function RequireAuthenticatedSession({ children }: { children: JSX.Element }) {
    const { me, loading, isLoggingOut } = useAuth();
    const loc = useLocation();
    if (loading) return <div className="p-6">세션 확인중…</div>;
    if (!me) {
        return (
            <Navigate
                to={isLoggingOut ? '/login?logged_out=1' : `/login?redirect=${encodeURIComponent(loc.pathname + loc.search)}`}
                replace
            />
        );
    }
    if (me.status === 'LOCKED') return <Navigate to="/locked" replace />;

    return children;
}

function RequireServiceAdmin({ children }: { children: JSX.Element }) {
    const { me, loading, isLoggingOut } = useAuth();
    const loc = useLocation();
    if (loading) return <div className="p-6">세션 확인 중...</div>;
    if (!me) {
        return (
            <Navigate
                to={isLoggingOut ? '/login?logged_out=1' : `/login?redirect=${encodeURIComponent(loc.pathname + loc.search)}`}
                replace
            />
        );
    }
    if (me.mustChangePassword) {
        return <Navigate to={`/change-password?redirect=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
    }
    if (!me.isServiceAdmin) return <Navigate to="/company" replace />;
    if (me.status === 'LOCKED') return <Navigate to="/locked" replace />;

    return children;
}

function RequireCompanyAdminOrService({ children }: { children: JSX.Element }) {
    const { me, loading, isLoggingOut } = useAuth();
    const loc = useLocation();
    if (loading) return <div className="p-6">세션 확인 중...</div>;
    if (!me) {
        return (
            <Navigate
                to={isLoggingOut ? '/login?logged_out=1' : `/login?redirect=${encodeURIComponent(loc.pathname + loc.search)}`}
                replace
            />
        );
    }
    if (me.mustChangePassword) {
        return <Navigate to={`/change-password?redirect=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
    }
    if (!(me.isServiceAdmin || me.isCompanyAdmin)) return <Navigate to="/projects" replace />;
    if (me.status === 'LOCKED') return <Navigate to="/locked" replace />;

    return children;
}

export default function AppRoutes() {
    return (
        <>
            <Helmet>
                <title>Project Management</title>
            </Helmet>
            <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="locked" element={<LockedPage />} />
                {/* <Route path="/" element={<AuthGate />} /> */}
                <Route path="/login" element={<LoginPage />} />
                <Route
                    path="/change-password"
                    element={
                        <RequireAuthenticatedSession>
                            <ChangePasswordPage />
                        </RequireAuthenticatedSession>
                    }
                />
                <Route
                    path="/account/security"
                    element={
                        <RequireAuthenticatedSession>
                            <ChangePasswordPage />
                        </RequireAuthenticatedSession>
                    }
                />

                {/* 역할별 홈 */}

                {/* 1) Super Admin */}
                <Route
                    path="/admin"
                    element={
                        <RequireServiceAdmin>
                            <AdminLayout />
                        </RequireServiceAdmin>
                    }
                >
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<AdminOverviewPage />} />
                    <Route path="companies" element={<AdminCompaniesPage />} />
                    <Route path="users" element={<AdminUsersPage />} />
                    <Route path="roles" element={<AdminRolesPage />} />
                    <Route path="permissions" element={<AdminPermissionsPage />} />
                </Route>

                {/* 2) Company Admin (회사 단위) */}
                <Route
                    path="/company"
                    element={
                        <RequireCompanyAdminOrService>
                            <CompanyAdminLayout />
                        </RequireCompanyAdminOrService>
                    }
                >
                    <Route index element={<Navigate to="profile" replace />} />
                    <Route path="profile" element={<CompanyProfilePage />} />
                    <Route path="partners" element={<CompanyPartnersPage />} />
                    <Route path="users" element={<CompanyUsersPage />} />
                    <Route path="projects" element={<CompanyProjectsPage />} />
                </Route>

                {/* 3) 프로젝트 목록(전역) */}
                <Route
                    path="/projects"
                    element={
                        <RequireAuth>
                            {/* <AllProjectsDashboard /> */}
                            <ProjectsPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/projects/overview"
                    element={
                        <RequireAuth>
                            <AllProjectsDashboard />
                        </RequireAuth>
                    }
                />

                {/* 4) 프로젝트 단위(중첩) */}
                <Route
                    path="/projects/:projectId"
                    element={
                        <RequireAuth>
                            <ProjectLayout />
                        </RequireAuth>
                    }
                >
                    <Route index element={<ProjectDashboard />} />
                    <Route path="dashboard" element={<ProjectDashboard />} />
                    {/* 3) Projecgt Admin (프로젝트 단위 Settings) */}
                    <Route
                        path="settings"
                        element={
                            <RequireProjectPermission perm="project.settings.open">
                                <ProjectSettingsLayout />
                            </RequireProjectPermission>
                        }
                    >
                        <Route index element={<ProjectSettingsGeneralPage />} />
                        <Route path="general" element={<ProjectSettingsGeneralPage />} />
                        <Route path="members" element={<ProjectMembersPage />} />
                        <Route path="orgs" element={<ProjectOrgsPage />} />
                        <Route path="companies" element={<ProjectCompaniesPage />} />
                        <Route path="wbs-groups" element={<ProjectWbsGroupsPage />} />
                        <Route path="fx" element={<ProjectFxSettingsPage />} />
                    </Route>
                    <Route path="contracts" element={<ContractsPageV2 />} />
                    <Route path="contracts/:contractId" element={<ContractDetailPageV2 />} />
                    <Route path="payments" element={<PaymentsHomePage />} />
                    <Route path="payments/contracts/:contractId" element={<PaymentExecutionPage />} />
                    <Route path="wbs" element={<WbsPage />} />
                    <Route
                        path="wbs/internal-progress-entry"
                        element={
                            <RequireProjectPermission perm="wbs.ev.entry">
                                <WbsInternalProgressEntryPage />
                            </RequireProjectPermission>
                        }
                    />
                    <Route
                        path="wbs/publish"
                        element={
                            <RequireProjectPermission perm="wbs.edit">
                                <WbsPublishPage />
                            </RequireProjectPermission>
                        }
                    />
                    <Route
                        path="wbs/editor"
                        element={
                            <RequireProjectPermission perm="wbs.edit">
                                <WbsEditorPage />
                            </RequireProjectPermission>
                        }
                    />
                    <Route path="gantt" element={<GanttPage />} />
                    <Route path="tasks" element={<TaskPage />} />
                    <Route path="risks" element={<RiskPage />} />
                    <Route path="s-curve" element={<SCurvePage />} />
                </Route>

                {/* 5) 외부사용자 progress entry */}
                <Route
                    path="/projects/:projectId/wbs/progress-entry"
                    element={
                        <RequireAuth>
                            <WbsProgressEntryPage />
                        </RequireAuth>
                    }
                />

                {/* 필요해서 빼둔 전역 라우트 */}
                {/* <Route path="/projects/:projectId/payment/overview" element={<PaymentOverviewPage />} />
                <Route path="/projects/:projectId/payment/client" element={<ClientPaymentPage />} />
                <Route path="/projects/:projectId/payment/vendor/:partyId" element={<VendorPaymentPage />} /> */}
            </Routes>
        </>
    );
}
